<template>
  <div
    v-observe-visibility="visibilityChanged"
    class="board-item-list-content-wrap"
    :class="{ expanded: quickView, 'card-drag-over': draggingCard }"
    @mouseenter="showScrollBar"
    @mouseleave="hideScrollBar"
  >
    <div
      v-if="visible"
      v-show="!draggingCard || (draggingCard && !quickView)"
      class="board-list-title"
      :class="[
        { 'drop-allowed': allowedDropToHeader },
        { 'drop-target': headerIsDropTarget && !sprintItemHeader },
      ]"
    >
      <draggable
        v-if="draggableDisplay"
        v-model="arrayBoardList"
        :item-key="(i) => i.id"
        class="droppable-header"
        group="cards"
        :ghost-class="'header-card-drop'"
        :invert-swap="true"
        :sort="false"
        handle=".nonexistingchild"
        :data-list-id="boardList.id"
        :disabled="isMobile"
        @change="headerChangeHandler"
      >
        <template #item>
          <ListOfBoardsItemListTitleContent
            :key="boardList.id"
            :board-list-id="boardList.id"
            :board-list-title="boardList.title"
            :total-cards="boardListCards.length"
            :board-list-icon-style="boardListIconStyle"
          />
        </template>
      </draggable>
      <div
        v-if="!draggableDisplay"
        class="droppable-header"
      >
        <ListOfBoardsItemListTitleContent
          :key="boardList.id"
          :board-list-id="boardList.id"
          :board-list-title="boardList.title"
          :total-cards="boardListCards.length"
          :board-list-icon-style="boardListIconStyle"
        />
      </div>
    </div>
    <vue-scroll
      v-if="expanded && !sprintItemHeader"
      ref="quickViewListContent"
      class="board-list-cards"
      :class="{ 'card-is-dragged-blc': draggingCard }"
      :ops="scrollOptions"
      @handle-scroll="handleScroll"
    >
      <draggable
        v-if="draggableDisplay"
        :id="boardList.id"
        v-model="boardListCards"
        :item-key="(cardId) => cardId"
        :data-list-id="boardList.id"
        group="cards"
        class="draggable-cards"
        :class="{
          'card-is-dragged': draggingCard,
          'list-cards-scrolled-down': scrollTop > 0,
        }"
        :animation="false"
        :revert-clone="true"
        :drag-class="'dragging-card'"
        :ghost-class="'list-card-drop'"
        draggable=".board-item-list-card, div.list-row"
        :prevent-on-filter="true"
        :fallback-on-body="true"
        :move="checkMove"
        :scroll-sensitivity="100"
        :force-fallback="true"
        :fallback-tolerance="10"
        :disabled="sprintDisableDrag || isGuestReader"
        @start="startDragging"
        @end="endDragging"
        @add="addCard"
        @update="moveCard"
      >
        <template #item="{ element, index: i }">
          <ListOfBoardsItemCard
            :key="element"
            class="list-row"
            :class="{ 'opacity-50 pointer-events-none': isRolledoverList }"
            :data-card-id="element"
            :data-list-id="listId"
            :data-board-id="(cards[element] || {}).board_id"
            :data-sprint-id="(cards[element] || {}).sprint_id"
            :card-id="element"
            :list="boardList"
            :index="i"
            :list-cards="boardListCards"
          />
        </template>
        <template #footer>
          <div
            v-if="draggingCard"
            draggable="false"
            class="droppable-header-in-list"
          >
            <ListOfBoardsItemListTitleContent
              :key="boardList.id"
              :board-list-id="boardList.id"
              :board-list-title="boardList.title"
              :total-cards="boardListCards.length"
              :board-list-icon-style="boardListIconStyle"
            />
          </div>
          <div
            v-if="!isRolledoverList && !isGuestReader"
            draggable="false"
            class="list-footer"
          >
            <DraftCardCreate
              class="list-row"
              :list-id="boardList.id"
              :list-title="boardList.title"
              :quick-view="true"
              :board-id="boardId"
              :preview-project-boards="previewProjectBoards"
              @scrollBoardListToEnd="scrollBoardListToEnd"
            />
            <AddNewCardButton :list-id="boardList.id" />
          </div>
        </template>
      </draggable>
      <div
        v-if="!draggableDisplay && !isRolledoverList"
        class="draggable-cards"
      >
        <ListOfBoardsItemCard
          v-for="(id, bIndex) in boardListCards"
          :key="id"
          :card-id="id"
          :list="boardList"
          :index="bIndex"
          :list-cards="boardListCards"
        />
        <div
          v-if="!isGuestReader"
          class="list-footer"
        >
          <DraftCardCreate
            class="list-row"
            :list-id="boardList.id"
            :list-title="boardList.title"
            :quick-view="true"
            :board-id="boardId"
            :preview-project-boards="previewProjectBoards"
            @scrollBoardListToEnd="scrollBoardListToEnd"
          />
          <AddNewCardButton :list-id="boardList.id" />
        </div>
      </div>
    </vue-scroll>
  </div>
</template>

<script>
import useSetAndOpenProject from '@/utilities/composables/useSetAndOpenProject';
import { handleUpdateEventBoardView, handleAddEventBoardView } from '@/utilities/moveCard';
import AddNewCardButton from '@/components/widgets/AddNewCardButton';
import DraftCardCreate from '@/components/widgets/DraftCardCreate';
import draggingEnd from '@/mixins/draggingEnd';
import customIconsMixin from '@/mixins/customIconsMixin';
import cardStatuses from '@/mixins/cardStatuses';
import ListOfBoardsItemListTitleContent from './ListOfBoardsItemListTitleContent';
import ListOfBoardsItemCard from './ListOfBoardsItemCard';
import EventBus from '@/utilities/eventBus';
import styleVariables from '@/assets/scss/variables.module.scss';
import { ResourceType } from '@/types/resources';
import useCurrentRole from '@/utilities/composables/useCurrentRole';

export default {
  name: 'ListOfBoardsItemList',
  components: {
    ListOfBoardsItemCard,
    ListOfBoardsItemListTitleContent,
    AddNewCardButton,
    DraftCardCreate,
  },

  mixins: [draggingEnd, customIconsMixin, cardStatuses],
  props: {
    listId: {
      type: String,
      required: true,
    },
    quickView: {
      type: Boolean,
      required: true,
      default: false,
    },
    boardId: {
      type: String,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    projectId: {
      type: String,
      required: false,
      default: '',
    },
    previewProjectBoards: {
      type: Boolean,
      required: false,
      default: false,
    },
    listType: {
      type: String,
      required: false,
      default: 'board',
    },
    sprintDisableDrag: {
      type: Boolean,
      required: false,
      default: false,
    },
    sprintItemHeader: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['verticalScrolling'],

  setup() {
    const { setAndOpenProject } = useSetAndOpenProject();
    const { isGuestReader } = useCurrentRole();

    return { setAndOpenProject, isGuestReader };
  },

  data() {
    return {
      addedCards: [],
      draggableDisplay: false,
      draggableDisplayTimeout: null,
      visible: this.index < 9,
      hideAllScrollbars: false,
      scrollOptions: {
        vuescroll: {
          detectResize: false,
        },
        scrollPanel: {
          scrollingX: false,
          scrollingY: true,
        },
        rail: {
          gutterOfSide: '0px',
          gutterOfEnds: '4px',
        },
        bar: {
          keepShow: false,
          background: 'rgba(69, 69, 69, 0.16)',
        },
      },
      scrollTop: 0,
      dragOriginBoard: false,
    };
  },
  computed: {
    arrayBoardList: {
      get() {
        return [this.boardList];
      },
      set() {
        // This is intentional settter that doesn't mutate arrayBoardList
      },
    },
    isSprint() {
      return this.listType === ResourceType.Sprint;
    },
    boardList() {
      return this.$store.getters.lists[this.listId] || {};
    },

    isRolledoverList() {
      return this.boardList.orginalId === this.$constants.rolledOverList;
    },

    boardListCards: {
      get() {
        // filter out dragged card from the destination list
        if (this.$store.getters.displayProjectPreviewBoards) {
          const dragged = this.$store.getters.draggedEntity;
          if (
            dragged.entityName === 'card' &&
            this.listId === dragged.listId &&
            !this.dragOriginBoard
          ) {
            const filtered = this.listCards.filter((c) => c !== dragged.cardId);
            return filtered;
          }
        }
        return this.listCards;
      },
      set() {
        // This is intentional settter that doesn't mutate boardListCards
      },
    },
    listCards() {
      return this.$store.getters.getListCards[this.listId] || [];
    },
    teamId() {
      return this.$store.getters.getTeamID;
    },
    allowedDropToHeader() {
      return this.draggingCard && !this.sprintItemHeader;
    },
    onDifferentRoute() {
      return (
        (this.$route.name !== this.$constants.routeNames.space &&
          this.$route.name !== this.$constants.routeNames.sprintsRouteName) ||
        this.$store.getters.displayProjectPreviewBoards
      );
    },
    draggingCard() {
      // when route is different than project homepage or project preview is displayed return false
      if (this.onDifferentRoute) return false;
      return this.draggedEntity.entityName === 'card';
    },
    headerIsDropTarget() {
      if (this.quickView || this.onDifferentRoute) {
        return false;
      }
      return this.$store.getters.dropTargetId === `l-${this.boardList.id}` && this.draggingCard;
    },
    expanded() {
      return this.$store.getters.boardExpanded(this.boardId);
    },

    cards() {
      return this.$store.getters.getCards;
    },

    previewProject() {
      return this.$store.getters.displayProjectPreviewBoards;
    },

    boardListIcon() {
      return (
        this.boardList.icon ||
        (this.cardStatuses[this.boardList.behavior] || {}).icon ||
        this.cardStatuses.backlog.icon ||
        ''
      );
    },

    boardListColor() {
      return (
        this.boardList.color ||
        (this.cardStatuses[this.boardList.behavior] || {}).color ||
        this.cardStatuses.backlog.color ||
        ''
      );
    },

    boardListIconStyle() {
      return {
        ...this.getCustomIconStyle(this.iconPath(this.boardListIcon)),
        background: this.boardListColor,
      };
    },

    isSprints() {
      return this.$route.name === this.$constants.routeNames.sprintsRouteName;
    },
  },
  watch: {
    draggedEntity(value) {
      if (this.onDifferentRoute) {
        return;
      }
      if (value.entityName === 'list') {
        this.hideAllScrollbars = true;
        this.hideScrollBar();
      } else {
        this.hideAllScrollbars = false;
      }
    },
    draggingCard(val) {
      if (val) {
        this.scrollOptions.rail.gutterOfEnds = styleVariables.listCardsGutterSize;
        return;
      }
      this.scrollOptions.rail.gutterOfEnds = '4px';
    },
  },
  created() {
    this.scrollOptions.rail.gutterOfEnds = this.draggingCard
      ? styleVariables.listCardsGutterSize
      : '4px';
  },
  mounted() {
    this.enableDraggable();
  },
  unmounted() {
    clearTimeout(this.draggableDisplayTimeout);
  },
  methods: {
    addCard(evt) {
      const { item, newDraggableIndex, oldDraggableIndex, oldIndex, newIndex } = evt;
      const element = this.cards[item.dataset.cardId];

      this.addCardHandler({
        item,
        element,
        newIndex: this.previewProject ? newIndex : newDraggableIndex,
        oldIndex: this.previewProject ? oldIndex : oldDraggableIndex,
      });
    },

    moveCard(evt) {
      const { item, newDraggableIndex, oldDraggableIndex } = evt;
      const listId = item.dataset.listId;

      handleUpdateEventBoardView({
        newIndex: newDraggableIndex,
        oldIndex: oldDraggableIndex,
        cardId: item.dataset.cardId,
        listId: listId,
        storeGetters: this.$store.getters,
        storeDispatch: this.$store.dispatch,
      });

      this.updateCardsOrder({
        cardId: item.dataset.cardId,
        newIndex: newDraggableIndex,
        oldIndex: oldDraggableIndex,
      }).catch(() => {
        handleUpdateEventBoardView({
          newIndex: oldDraggableIndex,
          oldIndex: newDraggableIndex,
          cardId: item.dataset.cardId,
          listId: listId,
          storeGetters: this.$store.getters,
          storeDispatch: this.$store.dispatch,
        });
      });
    },

    addCardHandler({ item, element, newIndex, oldIndex }) {
      if (this.$store.getters.dropTargetId.includes('pr-')) {
        const notification = {
          message: this.translate('cardCannotBeMovedDirectlyToSpace'),
          status: this.$constants.uiNotificationStatuses.error,
          duration: 3000,
        };
        this.$store.dispatch('addUiNotification', notification);
        return;
      }
      if (this.$store.getters.dropTargetId === '') {
        return;
      }

      const originalBoardId = element.board_id || element.sprint_id;
      const originalListId = item?.dataset?.listId || element.list_id;
      const destinationBoard = this.$store.getters.getFlatBoards[this.boardId];

      handleAddEventBoardView({
        cardId: element.id,
        listId: this.boardList.id,
        originalListId,
        originalBoardId,
        destinationBoardId: this.boardId,
        newIndex,
        oldIndex,
        storeGetters: this.$store.getters,
        storeDispatch: this.$store.dispatch,
      });

      const card = { ...this.$store.getters.getCards[element.id] };
      if (this.boardId !== originalBoardId) {
        this.$store.dispatch('removeCardFromGroupByBoard', { boardId: originalBoardId, card });
        this.$store.dispatch('addCardToGroupByBoard', { boardId: this.boardId, card });
      }

      this.addCardToList({
        listId: this.boardList.id,
        cardId: element.id,
        position: newIndex,
        // ...(this.boardId !== originalBoardId || (this.isSprints && { itemId: this.boardId })),
        itemId: destinationBoard.orginalId || this.boardId,
        projectId: card.project_id,
      })
        .then(() => {
          if (this.$store.getters.displayProjectPreviewBoards) {
            const project = this.$store.getters.getMyProjectById(card.project_id);
            this.setAndOpenProject(card.project_id).then(() => {
              this.$store.dispatch('setProjectMembers', project.members);
              this.$store.dispatch('displayProjectPreviewBoards', false);
              this.$store.dispatch('setProjectPreview', {});
            });
            // Retain scroll position of space after dragging card into it
            EventBus.$emit('saveLastScrollPosition');
          }
        })
        .catch(() => {
          handleAddEventBoardView({
            cardId: element.id,
            listId: originalListId,
            originalListId: this.boardList.id,
            originalBoardId: this.boardId,
            destinationBoardId: originalBoardId,
            newIndex: oldIndex,
            oldIndex: newIndex,
            storeGetters: this.$store.getters,
            storeDispatch: this.$store.dispatch,
          });
        });
    },

    headerChangeHandler(evt) {
      if (evt.added && !this.sprintItemHeader) {
        const { element } = evt.added;

        const { getListCards } = this.$store.getters;
        const card = { ...this.$store.getters.getCards[element] };

        this.addCardHandler({ element: card, newIndex: getListCards[this.boardList.id].length });
      }
    },

    addCardToList({ listId, cardId, position, itemId, projectId }) {
      const listForReq = this.isSprint ? this.$store.getters.lists[listId]?.orginalId : listId;
      return this.$store.dispatch('updateCard', {
        cardId,
        card: {
          position,
          list_id: listForReq,
          ...(!this.isSprint && itemId && { board_id: itemId }),
          ...(this.isSprint && itemId && { sprint_id: itemId }),
          ...(this.isSprint && itemId && { project_id: projectId }),
        },
      });
    },

    closeAddCardIfOpen() {
      if (this.$store.getters.getListDraftCardCreateView) {
        this.$store.dispatch('setListDraftCardCreateView', '');
      }
    },

    updateCardsOrder({ cardId, newIndex }) {
      return this.$store.dispatch('updateCard', { cardId, card: { position: newIndex } });
    },
    startDragging(evt) {
      this.dragOriginBoard = true;
      this.closeAddCardIfOpen();
      if (!this.quickView) return;
      // save which entity is dragged and what is type of the entity
      // save boardList.id as id, to prevent droping to same list header title
      this.$store.dispatch('setDraggedEntity', {
        entityName: 'card',
        id: this.boardList.id,
        listId: this.boardList.id,
        cardId: evt.clone.dataset.cardId,
        boardId: this.boardList?.board_id,
      });
    },
    endDragging() {
      this.dragOriginBoard = false;
      this.onDraggingEnd();
      if (
        this.$store.getters.dropTargetId.includes('pr-') ||
        this.$store.getters.dropTargetId === ''
      ) {
        this.$store.dispatch('displayProjectPreviewBoards', false);
        this.$store.dispatch('setProjectPreview', {});
      }
      // remove previously saved entity that is dragged and it's type
      this.$store.dispatch('setDraggedEntity', {});
      this.$store.dispatch('setDropTargetId', '');
    },
    checkMove(evt) {
      if (evt.to.className.includes('draggable-cards')) {
        this.$store.dispatch('setDropTargetId', `l-${evt.to.dataset.listId}`);
      }
      if (evt.to.className.includes('droppable-header')) {
        this.$store.dispatch('setDropTargetId', `l-${evt.to.dataset.listId}`);
      }
      return true;
    },
    enableDraggable() {
      this.draggableDisplayTimeout = setTimeout(() => {
        this.draggableDisplay = true;
        this.scrollOptions.vuescroll.detectResize = true;
      }, 600);
    },
    visibilityChanged(visible) {
      if (!visible) return;
      this.visible = visible;
    },
    showScrollBar() {
      if (this.hideAllScrollbars) return;
      this.scrollOptions.bar.keepShow = true;
    },
    hideScrollBar() {
      this.scrollOptions.bar.keepShow = false;
    },
    handleScroll(e) {
      this.scrollTop = e.scrollTop;
      this.$emit('verticalScrolling', { listRef: this.$refs.quickViewListContent });
    },
    scrollBoardListToEnd(listId) {
      if (listId !== this.boardList.id) return;
      this.$refs.quickViewListContent.scrollTo({
        y: '100%',
      });
    },
  },
};
</script>

<style lang="scss">
$listHeight: 334px;

.board-item-list-content-wrap {
  @apply shrink-0 rounded-lg pt-3;
  max-width: $boardColumnWidth;
  border-radius: 8px;
  flex-shrink: 0;

  &.expanded {
    height: auto;

    .board-list-title {
      opacity: 1;
    }
  }

  &.sortable-drag {
    box-shadow: 0px 2px 6px rgba(69, 69, 69, 0.11);
    width: $boardColumnWidth;
    height: auto !important;
    box-sizing: border-box;
    @apply bg-draggElement-background;

    .board-list-cards {
      height: auto !important;
      max-height: $listHeight;
      &.__vuescroll {
        .__rail-is-vertical {
          display: none !important;
        }
      }

      .draggable-cards {
        min-height: unset;
      }
    }
    .list-footer {
      border-radius: 0px 0px 8px 8px;
      background-color: var(--draggElementBg) !important;
    }
  }

  &.sortable-ghost {
    width: $boardColumnWidth !important;
    height: auto !important;
    @apply bg-important-hover;

    &.expanded {
      border-radius: 8px;
      > * {
        visibility: hidden;
      }

      .board-list-cards {
        height: auto !important;
        max-height: $listHeight;
        .draggable-cards {
          min-height: unset;
        }
      }
    }
  }

  .board-list-title {
    opacity: 0.8;
    transition: opacity 0.3s ease-in-out;
    will-change: opacity;
    transform: translate3d(0, 0, 0);
    font-size: 20px;
    color: #cbcbcb;
    overflow: hidden;
    padding-bottom: $titleListItemsGap;
    padding-left: $boardColumnPaddingLeft;
    padding-right: $boardColumnPaddingRight;
    text-overflow: ellipsis;
    box-sizing: border-box;

    .droppable-header {
      display: flex;
      flex-direction: column;
    }

    .header-card-drop {
      display: none;
    }

    &.drop-target {
      background-color: rgba(0, 0, 0, 0.04);
      border-radius: 12px;
      opacity: 1;
      padding-bottom: 0px;
      width: $columnWidth;
    }
  }
  .board-list-cards {
    display: flex;
    flex-direction: column;
    height: $listHeight !important;

    .__rail-is-vertical {
      bottom: 50px !important;
    }
    &.card-is-dragged-blc {
      min-height: 358px;

      .draggable-cards {
        .droppable-header-in-list {
          pointer-events: none;
        }
      }
    }

    .draggable-cards {
      display: flex;
      flex-direction: column;
      width: 100%;

      .droppable-header-in-list {
        position: sticky;
        top: 0;
        z-index: 3;
        padding-left: $boardColumnPaddingLeft;
        padding-right: $boardColumnPaddingRight;
        padding-bottom: $titleListItemsGap;
        order: 1;
        width: $columnWidth;
        @apply bg-important-bg;
      }

      .list-footer {
        @apply order-5 z-[3] sticky bottom-0 bg-important-bg mr-3;
        padding: 4px 0px 14px $boardColumnPaddingLeft;

        .draft-card-create {
          height: unset;

          .create-add-button {
            padding: 4px 8px;

            .shortcut-key-pill {
              height: 18px !important;
              min-width: 18px !important;

              span {
                line-height: 18px;
              }
            }
          }
        }

        .card-create-publish-button {
          width: unset;
          margin-top: 0px;
          margin-bottom: $boardCardGap;
        }

        .create-add-button {
          @apply hover:bg-important-hover;
        }
      }

      .list-card-drop {
        border-radius: 4px !important;
        margin: 4px $boardColumnPaddingRight 4px $boardColumnPaddingLeft;

        &.card-layout-list {
          &.sortable-chosen {
            background-color: transparent !important;

            .board-list-card-wrap {
              border-radius: 4px !important;
            }
          }
        }
      }

      .board-item-list-card,
      .list-card-drop {
        order: 2;
      }

      &.card-is-dragged {
        min-height: 358px;

        > .board-item-list-card,
        > .list-card-drop {
          margin-top: -36px;
          z-index: 2;
          padding-top: 40px;

          ~ .board-item-list-card,
          ~ .list-card-drop {
            margin-top: 4px;
            z-index: unset;
            padding-top: 0px;
          }
        }

        .list-card-drop {
          margin-top: 4px;
          padding-top: 0;
        }

        &.list-cards-scrolled-down {
          .droppable-header-in-list {
            z-index: 3;
            box-shadow: 0px 0px 20px -15px #212121;
            clip-path: inset(0px 0px -20px 0px);
          }
        }
      }
    }
  }

  &:last-child {
    &.card-drag-over {
      flex-grow: 1;

      .board-list-cards {
        .__rail-is-vertical {
          left: 267px;
        }
      }
    }
  }
}

.dragging-card {
  opacity: 1 !important;
  .board-item-list-card,
  .list-card-drop {
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
    background-color: #f8f8f8;
    border-radius: 12px;
    position: relative;

    .cancel-drag {
      position: absolute;
      top: 0;
      right: 0;
      display: block;
    }
  }

  .cancel-drag {
    display: flex;
  }
  .card-item-separator {
    display: none;
  }

  .board-item-list-card-content {
    &.card-grid-view {
      width: 100%;
    }
  }
}
</style>
